import { basename } from 'node:path';
import {
  sanitizeAiError,
  sanitizeAiErrorMessage,
  type ChatTurn,
  type FutureXClient,
  type OpenSessionResponse,
  type StreamEvent,
  type ToolResultMessage,
} from '@fim/futurex-protocol';
import type { Credentials } from '../creds.js';
import { refresh } from '../auth.js';
import { measureFileChange } from '../churn.js';
import { parseSse } from '../http.js';
import { buildRepoCard, readMemory } from '../repo.js';
import { createToolRunner, type Gate } from '../tool-runner.js';
import { line, toolCallLine, toolResultLine } from '../ui.js';

export interface GatewayClientDeps {
  /** Permission gate for mutating tools (the gateway never executes them). */
  readonly gate: Gate;
  /** Repository root the tools are confined to. */
  readonly repoRoot: string;
  /** Include the repo card in the session prefix (default true). */
  readonly useRepo?: boolean;
  /** Include futurex-memory.md in the session prefix (default true). */
  readonly useMemory?: boolean;
  /**
   * Observe each tool the gateway asked this client to run (after it ran).
   * The completion engine uses this to know when (and how much) files changed,
   * so it can decide whether to verify before letting a turn finish. `path`,
   * `deleted`, and `churn` are present for file writes/deletes.
   */
  readonly onTool?: (info: {
    name: string;
    mutating: boolean;
    ok: boolean;
    path?: string;
    deleted?: boolean;
    churn?: number;
    created?: boolean;
  }) => void;
}

/**
 * Turn a non-2xx gateway response into a clear, actionable message by reading
 * the API's `{ error: { code, message } }` envelope. The gateway gates FutureX
 * behind the subscription/tier checks, so surface those (and what to do)
 * instead of a bare status code.
 */
async function gatewayError(res: Response, fallback: string): Promise<string> {
  let code: string | undefined;
  let message: string | undefined;
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } };
    code = body.error?.code;
    message = body.error?.message;
  } catch {
    /* non-JSON body — fall back to the status code */
  }
  if (code === 'DAILY_LIMIT_REACHED') {
    return message ?? 'Daily token limit reached — it resets at 00:00 UTC. Try again then.';
  }
  if (code === 'TOKEN_CAP_REACHED') {
    return message ?? 'Monthly token limit reached — top up or upgrade in the dashboard.';
  }
  if (code === 'SUBSCRIPTION_REQUIRED' || res.status === 402) {
    return `${message ?? 'A FutureX subscription is required.'}\n  Subscribe (the Free plan is enough to start) in the dashboard, then run this again.`;
  }
  // 403 from a tier check already carries an actionable "…upgrade to use it." message.
  if (res.status === 403) {
    return message ?? 'Your account is not permitted to use FutureX.';
  }
  return message ?? fallback;
}

/**
 * Gateway-backed agent client — the thin client's whole backend.
 *
 * Opens a FutureX session (advertising the repo card, project memory, and the
 * tools it can execute), POSTs each turn, and relays the SSE stream. The CLI
 * never calls DeepSeek: the gateway runs the agent loop and, whenever its model
 * needs the repo, emits a `tool_request` — which this client runs LOCALLY
 * (Zod-validated, mutating tools permission-gated) and answers via
 * `POST …/tool-result`. Source never leaves the machine beyond what the model
 * explicitly reads. Silently refreshes the access token once on a 401.
 */
export function createGatewayClient(initial: Credentials, deps: GatewayClientDeps): FutureXClient {
  let creds = initial;
  let sessionId: string | null = null;
  const runner = createToolRunner(deps.repoRoot);
  const mutating = new Set(runner.descriptors().filter((t) => t.mutating).map((t) => t.name));

  async function authedFetch(path: string, init: RequestInit, allowRefresh = true): Promise<Response> {
    const res = await fetch(`${creds.apiUrl}${path}`, {
      ...init,
      headers: { ...(init.headers as Record<string, string>), authorization: `Bearer ${creds.accessToken}` },
    });
    if (res.status === 401 && allowRefresh) {
      creds = await refresh(creds);
      return authedFetch(path, init, false);
    }
    return res;
  }

  async function ensureSession(): Promise<string> {
    if (sessionId) return sessionId;
    const card = deps.useRepo === false ? undefined : (buildRepoCard(deps.repoRoot) ?? undefined);
    const memory = deps.useMemory === false ? undefined : (readMemory(deps.repoRoot) ?? undefined);
    const res = await authedFetch('/api/v1/futurex/sessions', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        repoName: basename(deps.repoRoot),
        ...(card ? { repoCard: card } : {}),
        ...(memory ? { memory } : {}),
        tools: runner.descriptors(),
      }),
    });
    if (!res.ok) throw new Error(await gatewayError(res, `failed to open session (${res.status})`));
    const json = (await res.json()) as { data: OpenSessionResponse };
    sessionId = json.data.sessionId;
    return sessionId;
  }

  async function postToolResult(id: string, message: ToolResultMessage): Promise<void> {
    try {
      await authedFetch(`/api/v1/futurex/sessions/${id}/tool-result`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(message),
      });
    } catch {
      // The loop's per-tool timeout will surface a clean failure if this is lost.
    }
  }

  /** Parse the model's (untrusted) tool args for change measurement; {} on error. */
  function safeArgs(argsJson: string): { path?: unknown; content?: unknown } {
    try {
      return JSON.parse(argsJson || '{}') as { path?: unknown; content?: unknown };
    } catch {
      return {};
    }
  }

  /** Run a tool the gateway asked for, print its activity, and POST the result. */
  async function handleToolRequest(
    id: string,
    requestId: string,
    ev: Extract<StreamEvent, { t: 'tool_request' }>,
  ): Promise<void> {
    const preview = runner.preview(ev.name, ev.arguments) ?? ev.name;
    const isMutating = mutating.has(ev.name);
    line(toolCallLine(preview, isMutating));
    // Size the change BEFORE the tool runs (the baseline is the pre-change file).
    const change = measureFileChange(deps.repoRoot, ev.name, safeArgs(ev.arguments));
    const result = await runner.run(ev.name, ev.arguments, deps.gate);
    line(toolResultLine(result.output, result.ok));
    deps.onTool?.({ name: ev.name, mutating: isMutating, ok: result.ok, ...(change ?? {}) });
    await postToolResult(id, { requestId, toolCallId: ev.id, ok: result.ok, output: result.output });
  }

  return {
    async *chat(turn: ChatTurn): AsyncIterable<StreamEvent> {
      try {
        const id = await ensureSession();
        const init: RequestInit = {
          method: 'POST',
          headers: { 'content-type': 'application/json', accept: 'text/event-stream' },
          // The gateway OWNS the system prefix (built from card + memory at open),
          // so the turn carries only the conversation + routing — never `system`.
          body: JSON.stringify({
            messages: turn.messages,
            reasoning: turn.reasoning,
            model: turn.model,
            maxTokens: turn.maxTokens,
            tier: turn.tier,
            effort: turn.effort,
            // Only the user's first round carries the real prompt; the gateway
            // audit-logs the prompt only when this is set (skips loop nudges).
            ...(turn.newTurn ? { newTurn: true } : {}),
          }),
        };
        if (turn.signal) init.signal = turn.signal;

        const res = await authedFetch(`/api/v1/futurex/sessions/${id}/chat`, init);
        if (!res.ok) {
          yield {
            t: 'error',
            message: sanitizeAiErrorMessage(await gatewayError(res, `gateway error (${res.status})`)),
          };
          return;
        }
        if (!res.body) {
          yield { t: 'error', message: `gateway error (${res.status})` };
          return;
        }
        const requestId = res.headers.get('x-futurex-request-id') ?? '';

        for await (const data of parseSse(res.body)) {
          if (data === '[DONE]') break;
          let event: StreamEvent;
          try {
            event = JSON.parse(data) as StreamEvent;
          } catch {
            continue;
          }
          if (event.t === 'tool_request') {
            // Execute locally + answer the gateway; don't surface it as chat output.
            await handleToolRequest(id, requestId, event);
            continue;
          }
          if (event.t === 'status') continue; // transport status (e.g. "open")
          // Brand-scrub error frames locally too, so a CLI pointed at an older
          // gateway never prints the raw provider error.
          if (event.t === 'error') {
            yield { t: 'error', message: sanitizeAiErrorMessage(event.message) };
            continue;
          }
          yield event;
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        yield { t: 'error', message: sanitizeAiError(err) };
      }
    },
  };
}
